// File: src/pages/CreateStockTransfer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Spinner,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

// align with your shared modules
import {
  ProductListView, // (kept as-is in case other parts rely on it)
  _Get,
  WarehousetoWareHouseTrx,
  _GetDataBydatabaseName, // ← used to fetch raw product master (same API as RawProductList)
} from "../import/ApiEndPoint/ApiCalling";

import {
  WareahouseList_For_addProduct,
  viewProductionProductSection,
  Image_URL,
} from "../import/ApiEndPoint/Api";

// ---------- helpers ----------
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// Map a warehouse.productItems line to a unified object we can use in both
// FROM and TO lists (supports normal product and raw product).
const mapWarehouseProductItem = (it) => {
  const prodTitle =
    it?.productId?.Product_Title ||
    it?.rawProductId?.Product_Title ||
    it?.Product_Title ||
    "";

  const primaryUnit =
    it?.primaryUnit ||
    it?.productId?.primaryUnit ||
    it?.rawProductId?.primaryUnit ||
    it?.stockUnit ||
    "";

  const secondaryUnit =
    it?.secondaryUnit ||
    it?.productId?.secondaryUnit ||
    it?.rawProductId?.secondaryUnit ||
    "";

  const secondarySize =
    it?.secondarySize ||
    it?.productId?.secondarySize ||
    it?.rawProductId?.secondarySize ||
    "";

  const gst =
    it?.gstPercentage ??
    it?.productId?.gstPercentage ??
    it?.rawProductId?.gstPercentage ??
    "";

  const igst =
    typeof it?.igstTaxType === "boolean"
      ? it.igstTaxType
      : !!(it?.productId?.igstTaxType || it?.rawProductId?.igstTaxType);

  const mrp =
    it?.price ??
    it?.Product_MRP ??
    it?.productId?.Product_MRP ??
    it?.rawProductId?.Product_MRP ??
    0;

  const sectionId =
    it?.productId?.productSection ||
    it?.rawProductId?.productSection ||
    it?.productSection ||
    "";

  // product/raw ids can be either objects or plain strings depending on backend populate
  const prodId =
    (it?.productId && typeof it.productId === "object" && it.productId?._id) ||
    (typeof it?.productId === "string" ? it.productId : "") ||
    "";

  const rawId =
    (it?.rawProductId &&
      typeof it.rawProductId === "object" &&
      it.rawProductId?._id) ||
    (typeof it?.rawProductId === "string" ? it.rawProductId : "") ||
    "";

  return {
    // keep the original line for quantities & stock
    ...it,

    // display (may get enriched later)
    Product_Title: prodTitle || "",

    // catalog identifiers (VERY IMPORTANT)
    __prodId: prodId,
    __rawId: rawId,

    // section to filter by (may get enriched later)
    __section: sectionId || "",

    // enriched props
    __primaryUnit: primaryUnit,
    __secondaryUnit: secondaryUnit,
    __secondarySize: secondarySize,
    __igstTaxType: igst,
    __gst: gst,
    __mrp: toNum(mrp),
  };
};

// Get section id from a mapped warehouse item
const getSectionIdFromItem = (it) =>
  it?.__section ||
  it?.productId?.productSection ||
  it?.rawProductId?.productSection ||
  it?.productSection ||
  "";

// component
const CreateStockTransfer = () => {
  const navigate = useNavigate();

  const [stockTrxDate, setStockTrxDate] = useState("");

  // warehouses
  const [fromWarehouses, setFromWarehouses] = useState([]);
  const [toWarehouses, setToWarehouses] = useState([]);
  const [allWarehouses, setAllWarehouses] = useState([]);

  const [fromWHSel, setFromWHSel] = useState([]);
  const [toWHSel, setToWHSel] = useState([]);

  // products per WH (snapshots used to build options)
  const [productListFromWH, setProductListFromWH] = useState([]);
  const [productListToWH, setProductListToWH] = useState([]);

  // sections master (from Production Product Sections)
  const [sectionsMaster, setSectionsMaster] = useState([]); // [{_id,name,status}]
  const [fromWarehouseSections, setFromWarehouseSections] = useState([]);
  const [toWarehouseSections, setToWarehouseSections] = useState([]);

  // chosen sections
  const [fromSectionId, setFromSectionId] = useState("");
  const [toSectionId, setToSectionId] = useState("");

  // raw product master index: id -> raw product doc (from the same API RawProductList uses)
  const [rawMasterIndex, setRawMasterIndex] = useState({});

  const [loading, setLoading] = useState(false);

  const emptyRow = {
    product: "",
    productId: "", // catalog id (product or raw product) — SOURCE
    Product_Title: "",
    primaryUnit: "",
    secondaryUnit: "",
    secondarySize: "",
    igstTaxType: false,
    AvailaleQty: 0,
    transferQty: 1,
    price: 0,
    gstPercentage: "",
    totalprice: 0,

    // TO-side (optional mapping to different product id)
    toProductId: "", // catalog id at destination
    toProductTitle: "",
  };

  const [rows, setRows] = useState([{ ...emptyRow }]);

  // ===== init =====
  useEffect(() => {
    const init = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const db = String(userData?.database || "");

        // 1) Fetch Production Product Sections (single source of truth for dropdown)
        try {
          const url = `${Image_URL}${viewProductionProductSection}/${encodeURIComponent(
            db
          )}`;
          const res = await fetch(url, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });
          const data = res.ok ? await res.json() : {};
          const list = Array.isArray(data?.ProductionProductSection)
            ? data.ProductionProductSection
            : [];
          const active = list.filter(
            (s) => (s?.status || "Active") === "Active"
          );
          setSectionsMaster(
            active.map((s) => ({
              _id: String(s?._id),
              name: s?.name || "",
              status: s?.status || "Active",
            }))
          );
        } catch {
          setSectionsMaster([]);
        }

        // 2) Warehouses + visible sets
        const whRes = await _Get(
          WareahouseList_For_addProduct,
          userData?.database
        );
        const warehouses = Array.isArray(whRes?.Warehouse)
          ? whRes.Warehouse
          : [];
        setAllWarehouses(warehouses);

        let fromList = [];
        if (["MASTER", "SuperAdmin"].includes(userData?.rolename?.roleName)) {
          fromList = warehouses;
        } else {
          fromList = warehouses.filter(
            (w) => String(w?.created_by?._id) === String(userData?._id)
          );
        }
        setFromWarehouses(fromList);
        setToWarehouses(warehouses);

        // 3) Build raw product master index (id -> raw product doc)
        try {
          const rawRes = await _GetDataBydatabaseName(db);
          const rawList = Array.isArray(rawRes?.Product) ? rawRes.Product : [];
          const idx = {};
          rawList.forEach((p) => {
            if (p?._id) idx[String(p._id)] = { ...p };
          });
          setRawMasterIndex(idx);
        } catch {
          setRawMasterIndex({});
        }

        // optional: trigger any global caches you need
        await ProductListView(userData?._id, userData?.database);
      } catch {
        // noop
      }
    };

    init();
  }, []);

  // Helper: enrich a mapped warehouse item using raw master (fill Product_Title AND section)
  const enrichFromRawMaster = (item) => {
    // prefer existing data
    let next = { ...item };

    // prefer raw id if present
    const rawId = next.__rawId;

    if (rawId && rawMasterIndex[rawId]) {
      const rawDoc = rawMasterIndex[rawId];

      // Name (critical for dropdown display)
      if (!next.Product_Title || next.Product_Title === "Not Found") {
        next.Product_Title = rawDoc?.Product_Title || next.Product_Title || "";
      }

      // Section (critical for filtering by Production Product Section)
      if (!next.__section) {
        next.__section =
          rawDoc?.productSection ||
          rawDoc?.Product_Section ||
          rawDoc?.product_section ||
          "";
      }

      // Units (fallbacks)
      if (!next.__primaryUnit) {
        next.__primaryUnit =
          rawDoc?.primaryUnit || rawDoc?.stockUnit || next.__primaryUnit || "";
      }
      if (!next.__secondaryUnit) {
        next.__secondaryUnit =
          rawDoc?.secondaryUnit || next.__secondaryUnit || "";
      }
      if (!next.__secondarySize) {
        next.__secondarySize =
          rawDoc?.secondarySize || next.__secondarySize || "";
      }
      if (!next.__gst && next.__gst !== 0) {
        next.__gst = rawDoc?.gstPercentage ?? next.__gst ?? "";
      }
      if (
        typeof next.__igstTaxType !== "boolean" &&
        rawDoc?.igstTaxType != null
      ) {
        next.__igstTaxType = !!rawDoc.igstTaxType;
      }
      if (!next.__mrp && rawDoc?.Product_MRP) {
        next.__mrp = toNum(rawDoc.Product_MRP, next.__mrp);
      }
    }

    return next;
  };

  // If raw master arrives after WH was selected, re-enrich cached lists
  useEffect(() => {
    if (!rawMasterIndex || !Object.keys(rawMasterIndex).length) return;

    setProductListFromWH((prev) => prev.map(enrichFromRawMaster));
    setProductListToWH((prev) => prev.map(enrichFromRawMaster));
  }, [rawMasterIndex]);

  // --- FROM warehouse selection ---
  const onSelectFrom = (selectedList, selectedItem) => {
    setFromWHSel(selectedList);

    // Exclude chosen FROM from TO options
    const filteredTo = allWarehouses.filter(
      (w) => w?._id !== selectedItem?._id
    );
    setToWarehouses(filteredTo);

    // Build FROM product options snapshot and ENRICH from raw master
    const mapped = (selectedList?.[0]?.productItems || [])
      .map(mapWarehouseProductItem)
      .map(enrichFromRawMaster);

    setProductListFromWH(mapped);

    // Build FROM sections from what's physically present in this WH
    const ids = Array.from(
      new Set(
        mapped
          .map((it) => getSectionIdFromItem(it))
          .filter((id) => id && id !== "undefined")
      )
    );
    const sections = ids.map((id) => ({
      _id: id,
      name:
        sectionsMaster.find((s) => String(s?._id) === String(id))?.name || id,
    }));
    setFromWarehouseSections(sections);

    // reset FROM selection
    setFromSectionId("");
    setRows([{ ...emptyRow }]);
  };

  const onRemoveFrom = () => {
    setFromWHSel([]);
    setToWarehouses(allWarehouses);
    setProductListFromWH([]);
    setFromWarehouseSections([]);
    setFromSectionId("");
    setRows([{ ...emptyRow }]);
  };

  // --- TO warehouse selection ---
  const onSelectTo = (selectedList) => {
    setToWHSel(selectedList);

    // Build TO product snapshot (used to show/filter destination products) and ENRICH
    const mapped = (selectedList?.[0]?.productItems || [])
      .map(mapWarehouseProductItem)
      .map(enrichFromRawMaster);

    setProductListToWH(mapped);

    // IMPORTANT: Product Section (To) list is master (production product sections),
    // not just what exists in this warehouse
    setToWarehouseSections(sectionsMaster);

    // force user to pick To Section after WH(To)
    setToSectionId("");
    setRows((prev) =>
      prev.map((r) => ({ ...r, toProductId: "", toProductTitle: "" }))
    );
  };

  const onRemoveTo = () => {
    setToWHSel([]);
    setProductListToWH([]);
    setToWarehouseSections([]);
    setToSectionId("");
    setRows((prev) =>
      prev.map((r) => ({ ...r, toProductId: "", toProductTitle: "" }))
    );
  };

  // --- pick a FROM product for a row ---
  const onPickProduct = (_list, item, idx) => {
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };

      // catalog id (product or raw) — CRITICAL for backend match
      r.productId = item.__prodId || item.__rawId || "";

      // display fields
      r.Product_Title =
        item?.Product_Title ||
        item?.productId?.Product_Title ||
        item?.rawProductId?.Product_Title ||
        "";

      // units & taxes
      r.primaryUnit = item?.__primaryUnit || "";
      r.secondaryUnit = item?.__secondaryUnit || "";
      r.secondarySize = item?.__secondarySize || "";
      r.igstTaxType = !!item?.__igstTaxType;
      r.gstPercentage = item?.__gst ?? "";

      // available stock and pricing
      r.AvailaleQty = toNum(item?.currentStock, 0);
      const price = toNum(item?.__mrp, 0);
      r.price = price;

      // normalize qty and totals
      const qty = toNum(r.transferQty || 1, 1);
      r.transferQty = qty;
      r.totalprice = Number((price * qty).toFixed(2));

      next[idx] = r;
      return next;
    });
  };

  const onRemovePickedProduct = (_list, _item, idx) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...emptyRow };
      return next;
    });
  };

  // --- pick a TO product for a row (optional; will auto-pick same id) ---
  const onPickProductTo = (_list, item, idx) => {
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };
      // destination product id (catalog id of product or raw product)
      r.toProductId = item?.__prodId || item?.__rawId || "";
      r.toProductTitle =
        item?.Product_Title ||
        item?.productId?.Product_Title ||
        item?.rawProductId?.Product_Title ||
        "";
      next[idx] = r;
      return next;
    });
  };

  const onRemovePickedProductTo = (_list, _item, idx) => {
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };
      r.toProductId = "";
      r.toProductTitle = "";
      next[idx] = r;
      return next;
    });
  };

  // qty change
  const onQtyChange = (e, idx) => {
    const ask = toNum(e.target.value, 0);
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };
      const available = toNum(r.AvailaleQty, 0);

      if (ask < 0) return prev;

      if (ask > available) {
        swal("Can not transfer more than available stock");
        r.transferQty = available > 0 ? available : 0;
      } else {
        r.transferQty = ask;
      }
      r.totalprice = Number(
        (toNum(r.price, 0) * toNum(r.transferQty, 0)).toFixed(2)
      );
      next[idx] = r;
      return next;
    });
  };

  // Snapshot of TO quantities/units for UI
  const getToSnapshot = (row) => {
    const { productId, toProductId, transferQty } = row;
    if (!productListToWH?.length) {
      return { available: "", primaryUnit: "", willBe: "" };
    }

    const match = productListToWH.find((p) => {
      const pid = p?.__prodId || p?.__rawId || "";
      const sec = getSectionIdFromItem(p);
      if (toProductId) return String(pid) === String(toProductId);
      const sameProduct = String(pid) === String(productId);
      return (
        sameProduct && (!toSectionId || String(sec) === String(toSectionId))
      );
    });

    const available = toNum(match?.currentStock, 0);
    const primaryUnit =
      match?.__primaryUnit ||
      match?.primaryUnit ||
      match?.productId?.primaryUnit ||
      match?.rawProductId?.primaryUnit ||
      "";
    const willBe = toNum(available + toNum(transferQty, 0), 0);

    return { available, primaryUnit, willBe };
  };

  // row add/remove
  const addRow = () => setRows((prev) => [...prev, { ...emptyRow }]);
  const removeRow = (i) =>
    setRows((prev) => {
      const next = [...prev];
      next.splice(i, 1);
      return next.length ? next : prev;
    });

  // FROM product options filtered by FROM section
  const fromProductOptions = useMemo(() => {
    if (!fromSectionId) return [];
    return (productListFromWH || []).filter(
      (it) => String(getSectionIdFromItem(it)) === String(fromSectionId)
    );
  }, [productListFromWH, fromSectionId]);

  // TO product options filtered by TO section (dropdown is filled from Production list)
  const toProductOptions = useMemo(() => {
    if (!toSectionId) return [];
    return (productListToWH || []).filter(
      (it) => String(getSectionIdFromItem(it)) === String(toSectionId)
    );
  }, [productListToWH, toSectionId]);

  // Auto-pick same product on TO if exists in selected TO section
  useEffect(() => {
    if (!toSectionId) {
      setRows((prev) =>
        prev.map((r) => ({ ...r, toProductId: "", toProductTitle: "" }))
      );
      return;
    }
    setRows((prev) =>
      prev.map((r) => {
        const same = toProductOptions.find((p) => {
          const pid = p?.__prodId || p?.__rawId || "";
          return String(pid) === String(r.productId);
        });
        if (same) {
          return {
            ...r,
            toProductId: same?.__prodId || same?.__rawId || "",
            toProductTitle:
              same?.Product_Title ||
              same?.productId?.Product_Title ||
              same?.rawProductId?.Product_Title ||
              "",
          };
        }
        return { ...r, toProductId: "", toProductTitle: "" };
      })
    );
  }, [toSectionId, toProductOptions]);

  // submit
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stockTrxDate) return swal("Please select a transfer date.");
    if (!fromWHSel?.length || !toWHSel?.length)
      return swal("Please select both warehouses.");
    if (!fromSectionId) return swal("Please select a FROM Product Section.");
    if (!toSectionId) return swal("Please select a TO Product Section.");
    if (!rows.every((r) => r.productId && toNum(r.transferQty, 0) > 0))
      return swal("Each row must have a product and quantity > 0.");

    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("userData") || "{}");

      const productItems = rows
        .filter((r) => r.productId)
        .map((r) => ({
          // SOURCE catalog id (MUST be productId or rawProductId present in source WH)
          productId: r.productId,

          // DEST catalog id if mapping differs (backend update step uses this)
          destinationProductId: r.toProductId || r.productId,

          unitType: r.unitType || "",
          primaryUnit: r.primaryUnit || "",
          secondaryUnit: r.secondaryUnit || "",
          secondarySize: r.secondarySize || "",

          price: toNum(r.price, 0),
          transferQty: toNum(r.transferQty, 0),
          totalPrice: toNum(r.totalprice, 0),

          gstPercentage: String(r.gstPercentage || ""),
          igstTaxType: !!r.igstTaxType,
        }));

      const payload = {
        productItems,
        warehouseToId: toWHSel?.[0]?._id,
        warehouseFromId: fromWHSel?.[0]?._id,
        fromSectionId,
        toSectionId,
        stockTransferDate: stockTrxDate,
        grandTotal: Number(
          productItems
            .reduce((a, b) => a + Number(b.totalPrice || 0), 0)
            .toFixed(2)
        ),
        transferStatus: "InProcess",
        created_by: user?._id,
      };

      await WarehousetoWareHouseTrx(payload);
      swal("Stock transfer is initiated");
      navigate(-1);
    } catch (err) {
      swal(
        err?.response?.data?.error ||
          err?.message ||
          "Something went wrong while creating stock transfer."
      );
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = rows
    .reduce((a, r) => a + toNum(r.totalprice, 0), 0)
    .toFixed(2);

  return (
    <div>
      <Card>
        {/* top bar */}
        <Row className="m-2">
          <Col lg="3" md="4" sm="12" className="mb-2 mt-1">
            <Label>Stock Transfer Date *</Label>
            <Input
              required
              type="date"
              value={stockTrxDate}
              onChange={(e) => setStockTrxDate(e.target.value)}
            />
          </Col>
          <Col className="mt-4">
            <div className="float-right">
              <Button color="primary" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>
          </Col>
        </Row>

        <CardBody>
          <Form className="mx-1" onSubmit={onSubmit}>
            {/* Warehouses */}
            <Row>
              <Col className="mb-1" lg="6" md="6" sm="12">
                <Label>Warehouse (From) *</Label>
                <Multiselect
                  required
                  selectionLimit={1}
                  isObject={true}
                  options={fromWarehouses}
                  onSelect={onSelectFrom}
                  onRemove={onRemoveFrom}
                  displayValue="warehouseName"
                />
              </Col>

              <Col className="mb-1" lg="6" md="6" sm="12">
                <Label>Warehouse (To) *</Label>
                <Multiselect
                  required
                  selectionLimit={1}
                  isObject={true}
                  options={toWarehouses}
                  onSelect={onSelectTo}
                  onRemove={onRemoveTo}
                  displayValue="warehouseName"
                />
              </Col>
            </Row>

            {/* Sections */}
            <Row>
              <Col className="mb-1" lg="6" md="6" sm="12">
                <Label>Product Section (From) *</Label>
                <Input
                  type="select"
                  value={fromSectionId}
                  onChange={(e) => setFromSectionId(e.target.value)}
                  disabled={!fromWHSel.length || !fromWarehouseSections.length}
                  required
                >
                  <option value="">-- Select Section --</option>
                  {fromWarehouseSections.map((s) => (
                    <option key={s?._id} value={s?._id}>
                      {s?.name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col className="mb-1" lg="6" md="6" sm="12">
                <Label>Product Section (To) *</Label>
                <Input
                  type="select"
                  value={toSectionId}
                  onChange={(e) => setToSectionId(e.target.value)}
                  // enabled immediately after Warehouse (To) is picked
                  disabled={!toWHSel.length || !toWarehouseSections.length}
                  required
                >
                  <option value="">-- Select Section --</option>
                  {toWarehouseSections.map((s) => (
                    <option key={s?._id} value={s?._id}>
                      {s?.name}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            {/* Two mirrored sections */}
            {rows.map((r, idx) => {
              const toSnap = getToSnapshot(r);
              return (
                <Row
                  key={idx}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 10,
                    marginTop: 10,
                  }}
                >
                  {/* FROM */}
                  <Col
                    lg="6"
                    md="12"
                    sm="12"
                    style={{ borderRight: "1px dashed #e5e7eb" }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      From Warehouse
                    </div>
                    <Row>
                      <Col className="mb-1" lg="12" md="12" sm="12">
                        <Label>
                          Product{" "}
                          <small style={{ color: "#6b7280" }}>
                            (filtered by selected section)
                          </small>
                        </Label>
                        <Multiselect
                          required
                          selectionLimit={1}
                          isObject={true}
                          options={fromProductOptions}
                          onSelect={(list, item) =>
                            onPickProduct(list, item, idx)
                          }
                          onRemove={(list, item) =>
                            onRemovePickedProduct(list, item, idx)
                          }
                          displayValue="Product_Title"
                          disable={fromSectionId === ""}
                        />
                      </Col>

                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Primary Unit</Label>
                        <Input
                          type="text"
                          readOnly
                          value={r?.primaryUnit || ""}
                        />
                      </Col>

                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Available Qty</Label>
                        <Input
                          type="number"
                          disabled
                          value={r?.AvailaleQty ?? ""}
                        />
                      </Col>

                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Qty Transfer</Label>
                        <Input
                          type="number"
                          min={0}
                          value={r?.transferQty}
                          onChange={(e) => onQtyChange(e, idx)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Price</Label>
                        <Input type="number" readOnly value={r?.price || 0} />
                      </Col>
                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>GST %</Label>
                        <Input
                          type="text"
                          readOnly
                          value={r?.gstPercentage || ""}
                        />
                      </Col>
                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Total</Label>
                        <Input
                          type="number"
                          readOnly
                          value={r?.totalprice || 0}
                        />
                      </Col>
                    </Row>
                  </Col>

                  {/* TO */}
                  <Col lg="6" md="12" sm="12">
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      To Warehouse (By Section)
                    </div>
                    <Row>
                      <Col className="mb-1" lg="12" md="12" sm="12">
                        <Label>
                          Product (To){" "}
                          <small style={{ color: "#6b7280" }}>
                            filtered by selected To Section
                          </small>
                        </Label>
                        <Multiselect
                          selectionLimit={1}
                          isObject={true}
                          options={toProductOptions}
                          onSelect={(list, item) =>
                            onPickProductTo(list, item, idx)
                          }
                          onRemove={(list, item) =>
                            onRemovePickedProductTo(list, item, idx)
                          }
                          displayValue="Product_Title"
                          disable={toSectionId === ""}
                        />
                      </Col>

                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Primary Unit</Label>
                        <Input
                          type="text"
                          readOnly
                          value={toSnap.primaryUnit}
                        />
                      </Col>

                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Current Qty</Label>
                        <Input
                          type="number"
                          readOnly
                          value={toSnap.available}
                        />
                      </Col>

                      <Col className="mb-1" lg="4" md="6" sm="6">
                        <Label>Qty After Transfer</Label>
                        <Input type="number" readOnly value={toSnap.willBe} />
                      </Col>
                    </Row>
                  </Col>

                  {/* row actions */}
                  <Col lg="12" className="mt-2 d-flex" style={{ gap: 8 }}>
                    {idx > 0 && (
                      <Button
                        color="danger"
                        type="button"
                        onClick={() => removeRow(idx)}
                      >
                        - Remove
                      </Button>
                    )}
                    <Button color="primary" type="button" onClick={addRow}>
                      + Add
                    </Button>
                  </Col>
                </Row>
              );
            })}

            {/* Totals */}
            <Row className="mt-3">
              <Col className="d-flex justify-content-end">
                <strong style={{ fontSize: 16 }}>
                  Grand Total: {grandTotal}
                </strong>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col className="d-flex justify-content-center">
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="mr-1" /> Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateStockTransfer;
